import React, { useEffect, useState } from 'react';
import {
  Clock,
  Play,
  Pause,
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader2,
  History,
  X,
  AlertTriangle,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import { useScheduledQueriesStore } from '@/stores/scheduledQueriesStore';
import { ScheduledQuerySummary } from '@/components/analytics/ScheduledQuerySummary';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { apiClient, type ScenarioSummary, type ScheduledQueryResponse, type ScheduledQueryResultResponse } from '@/utils/apiClient';
import { scheduleWriteBody, type ScheduleMode } from '@/utils/schedulePayload';

// Cron presets for the schedule picker (labels resolved via i18n)
const CRON_PRESET_DEFS = [
  { key: 'everyHour', value: '0 * * * *' },
  { key: 'every6Hours', value: '0 */6 * * *' },
  { key: 'dailyAt9am', value: '0 9 * * *' },
  { key: 'dailyAtMidnight', value: '0 0 * * *' },
  { key: 'everyMondayAt9am', value: '0 9 * * 1' },
  { key: 'everyWeekdayAt9am', value: '0 9 * * 1-5' },
  { key: 'firstOfMonthAt9am', value: '0 9 1 * *' },
] as const;

type CronPresetKey = (typeof CRON_PRESET_DEFS)[number]['key'];

function resolveCronPresets(sq: Record<string, any>) {
  const labels = sq.cronPresets ?? {};
  return CRON_PRESET_DEFS.map((p) => ({
    value: p.value,
    label: labels[p.key] ?? p.key,
  }));
}

function cronToHuman(cron: string, sq: Record<string, any>): string {
  const preset = CRON_PRESET_DEFS.find((p) => p.value === cron);
  if (preset) {
    return sq.cronPresets?.[preset.key as CronPresetKey] ?? cron;
  }
  return cron;
}

function statusLabel(status: string, sq: Record<string, any>): string {
  switch (status) {
    case 'success':
      return sq.statusSuccess ?? 'Success';
    case 'error':
      return sq.statusError ?? 'Error';
    case 'running':
      return sq.statusRunning ?? 'Running';
    default:
      return status;
  }
}

function statusIcon(status: string) {
  switch (status) {
    case 'success':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'error':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'running':
      return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    default:
      return <Clock className="h-4 w-4 text-gray-400" />;
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Create / Edit Modal
// ---------------------------------------------------------------------------
interface QueryFormData {
  name: string;
  mode: ScheduleMode;
  query_text: string;
  scenario_id: string;
  cron_expression: string;
  notification_method: string;
}

const emptyForm: QueryFormData = {
  name: '',
  mode: 'query',
  query_text: '',
  scenario_id: '',
  cron_expression: '0 9 * * *',
  notification_method: 'in_app',
};

function scenarioChoices(scenarios: ScenarioSummary[], selectedId: string): ScenarioSummary[] {
  if (!selectedId || scenarios.some((s) => s.id === selectedId)) return scenarios;
  return [{ id: selectedId, title: selectedId, description: '' }, ...scenarios];
}

interface FormModalProps {
  open: boolean;
  editing: ScheduledQueryResponse | null;
  scenarios: ScenarioSummary[];
  scenariosLoading: boolean;
  scenariosError: boolean;
  onClose: () => void;
  onSave: (data: QueryFormData) => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const FormModal: React.FC<FormModalProps> = ({
  open, editing, scenarios, scenariosLoading, scenariosError, onClose, onSave, error, clearError,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<QueryFormData>(emptyForm);
  const [customCron, setCustomCron] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name,
        mode: editing.scenario_id ? 'scenario' : 'query',
        query_text: editing.query_text ?? '',
        scenario_id: editing.scenario_id ?? '',
        cron_expression: editing.cron_expression,
        notification_method: editing.notification_method,
      });
      setCustomCron(!CRON_PRESET_DEFS.some((p) => p.value === editing.cron_expression));
    } else {
      setForm(emptyForm);
      setCustomCron(false);
    }
    setConfirming(false);
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, open]);

  if (!open) return null;

  const sq = (t as any).scheduledQueries ?? {};
  const cronPresets = resolveCronPresets(sq);

  // Step 1 — native validation passes, then open the human-in-the-loop confirm.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setConfirming(true);
  };

  // Step 2 — the confirmed write runs; on failure the modal stays open and the
  // store error is shown *inside* it (fixes STORY-156 "no response on create").
  const doSubmit = async () => {
    setSaving(true);
    try {
      await onSave(form);
      setConfirming(false);
      onClose();
    } catch {
      setConfirming(false); // error is rendered inside the modal below
    } finally {
      setSaving(false);
    }
  };

  const scheduleLabel =
    cronPresets.find((p) => p.value === form.cron_expression)?.label ?? form.cron_expression;
  const scenarioTitle =
    scenarios.find((s) => s.id === form.scenario_id)?.title ?? form.scenario_id;
  const sourceLine = form.mode === 'scenario'
    ? `${sq.scenario ?? 'Scenario'}: ${scenarioTitle}`
    : `${sq.queryText ?? 'Query'}: ${form.query_text}`;
  const confirmPreview = [
    `${sq.name ?? 'Name'}: ${form.name}`,
    `${sq.mode ?? 'Mode'}: ${form.mode === 'scenario' ? (sq.modeScenario ?? 'Scenario') : (sq.modeQuery ?? 'Query')}`,
    sourceLine,
    `${sq.schedule ?? 'Schedule'}: ${scheduleLabel}`,
    `${sq.notification ?? 'Notification'}: ${form.notification_method}`,
  ];
  const scenarioOptions = scenarioChoices(scenarios, form.scenario_id);
  const selectedScenario = scenarios.find((s) => s.id === form.scenario_id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {editing ? (t as any).scheduledQueries?.editQuery ?? 'Edit Scheduled Query' : (t as any).scheduledQueries?.createQuery ?? 'Create Scheduled Query'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Save error — shown inside the modal so a failed create isn't silent */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {(t as any).scheduledQueries?.name ?? 'Name'}
            </label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={(t as any).scheduledQueries?.namePlaceholder ?? 'My daily report'}
            />
          </div>

          {/* Query vs scenario */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {sq.mode ?? 'Mode'}
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                aria-pressed={form.mode === 'query'}
                onClick={() => setForm({ ...form, mode: 'query' })}
                className={clsx(
                  'flex-1 px-3 py-2 rounded-md border text-sm font-medium',
                  form.mode === 'query'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                )}
              >
                {sq.modeQuery ?? 'Query'}
              </button>
              <button
                type="button"
                aria-pressed={form.mode === 'scenario'}
                onClick={() => setForm({ ...form, mode: 'scenario' })}
                className={clsx(
                  'flex-1 px-3 py-2 rounded-md border text-sm font-medium',
                  form.mode === 'scenario'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                )}
              >
                {sq.modeScenario ?? 'Scenario'}
              </button>
            </div>
          </div>

          {form.mode === 'query' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {sq.queryText ?? 'Query Text'}
              </label>
              <textarea
                name="query_text"
                required
                rows={3}
                value={form.query_text}
                onChange={(e) => setForm({ ...form, query_text: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                placeholder={sq.queryPlaceholder ?? 'Show me hosts with CPU usage > 80%'}
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {sq.scenario ?? 'Scenario'}
              </label>
              {scenariosLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t.common.loading}</p>
              ) : scenariosError ? (
                <p className="text-sm text-red-600 dark:text-red-400">{sq.scenarioLoadError ?? 'Could not load scenarios'}</p>
              ) : scenarioOptions.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{sq.scenarioEmpty ?? 'No scenarios available'}</p>
              ) : (
                <>
                  <select
                    name="scenario_id"
                    required
                    value={form.scenario_id}
                    onChange={(e) => setForm({ ...form, scenario_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">{sq.scenarioPlaceholder ?? 'Select a scenario'}</option>
                    {scenarioOptions.map((s) => (
                      <option key={s.id} value={s.id}>{s.title}</option>
                    ))}
                  </select>
                  {selectedScenario?.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{selectedScenario.description}</p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {(t as any).scheduledQueries?.schedule ?? 'Schedule'}
            </label>
            {!customCron ? (
              <div className="space-y-2">
                <select
                  value={form.cron_expression}
                  onChange={(e) => setForm({ ...form, cron_expression: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {cronPresets.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  onClick={() => setCustomCron(true)}
                >
                  {(t as any).scheduledQueries?.customCron ?? 'Use custom cron expression'}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={form.cron_expression}
                  onChange={(e) => setForm({ ...form, cron_expression: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                  placeholder="0 9 * * 1"
                />
                <button
                  type="button"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  onClick={() => setCustomCron(false)}
                >
                  {(t as any).scheduledQueries?.usePreset ?? 'Use preset'}
                </button>
              </div>
            )}
          </div>

          {/* Notification method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {(t as any).scheduledQueries?.notification ?? 'Notification'}
            </label>
            <select
              value={form.notification_method}
              onChange={(e) => setForm({ ...form, notification_method: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="in_app">{(t as any).scheduledQueries?.notifyInApp ?? 'In-App'}</option>
              <option value="telegram">{(t as any).scheduledQueries?.notifyTelegram ?? 'Telegram'}</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {t.common.cancel}
            </button>
            <button
              type="submit"
              disabled={saving || (form.mode === 'scenario' && !form.scenario_id)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {t.common.save}
            </button>
          </div>
        </form>
      </div>

      {/* STORY-156 — human-in-the-loop confirmation before the scheduled-query write */}
      <ConfirmDialog
        isOpen={confirming}
        title={sq.confirmSaveTitle ?? 'Confirm scheduled query'}
        message={sq.confirmSaveMessage ?? 'Review this scheduled query before saving.'}
        preview={confirmPreview}
        confirmLabel={sq.confirmSaveButton ?? (t.common.confirm)}
        cancelLabel={t.common.back}
        loading={saving}
        onConfirm={doSubmit}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// History Modal
// ---------------------------------------------------------------------------
interface HistoryModalProps {
  open: boolean;
  queryName: string;
  history: ScheduledQueryResultResponse[];
  loading: boolean;
  onClose: () => void;
}

const HistoryModal: React.FC<HistoryModalProps> = ({ open, queryName, history, loading, onClose }) => {
  const { t } = useTranslation();
  const sq = (t as any).scheduledQueries ?? {};
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {sq.historyOf ?? 'History'}: {queryName}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              {sq.noHistory ?? 'No execution history yet.'}
            </p>
          ) : (
            <div className="space-y-3">
              {history.map((h) => (
                <div
                  key={h.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-md p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {statusIcon(h.status)}
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {statusLabel(h.status, sq)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDateTime(h.executed_at)}
                      {h.duration_ms != null && <span className="ml-2">({h.duration_ms}ms)</span>}
                    </div>
                  </div>
                  {h.result_text && (
                    <pre className="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded p-2 overflow-x-auto max-h-40 whitespace-pre-wrap">
                      {h.result_text}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Delete Confirmation Modal
// ---------------------------------------------------------------------------
interface DeleteConfirmModalProps {
  open: boolean;
  queryName: string;
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ open, queryName, onConfirm, onCancel, deleting }) => {
  const { t } = useTranslation();
  const sq = (t as any).scheduledQueries ?? {};
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm mx-4">
        <div className="p-5 text-center">
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {sq.deleteConfirmTitle ?? 'Delete Scheduled Query?'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            <span className="font-medium text-gray-700 dark:text-gray-300">{queryName}</span>
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-5">
            {sq.deleteConfirmHint ?? 'This will permanently remove the schedule and all its execution history. This cannot be undone.'}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={onCancel}
              disabled={deleting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              {t.common.cancel}
            </button>
            <button
              onClick={onConfirm}
              disabled={deleting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleting ? (sq.deleting ?? 'Deleting...') : (sq.remove ?? t.common.delete)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const ScheduledQueriesSettings: React.FC = () => {
  const { t, language } = useTranslation();
  const {
    queries,
    loading,
    error,
    selectedQueryHistory,
    historyLoading,
    fetchQueries,
    createQuery,
    updateQuery,
    removeQuery,
    runNow,
    fetchHistory,
    clearError,
  } = useScheduledQueriesStore();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ScheduledQueryResponse | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [historyQueryId, setHistoryQueryId] = useState<number | null>(null);
  const [historyQueryName, setHistoryQueryName] = useState('');
  const [runningIds, setRunningIds] = useState<Set<number>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<ScheduledQueryResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [scenarios, setScenarios] = useState<ScenarioSummary[]>([]);
  const [scenariosLoading, setScenariosLoading] = useState(true);
  const [scenariosError, setScenariosError] = useState(false);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  useEffect(() => {
    let cancelled = false;
    setScenariosLoading(true);
    setScenariosError(false);
    apiClient.listScenarios(language).then(
      (rows) => {
        if (!cancelled) setScenarios(rows);
      },
      () => {
        if (!cancelled) setScenariosError(true);
      },
    ).finally(() => {
      if (!cancelled) setScenariosLoading(false);
    });
    return () => { cancelled = true; };
  }, [language]);

  const handleCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = (q: ScheduledQueryResponse) => {
    setEditing(q);
    setShowForm(true);
  };

  const handleSave = async (data: QueryFormData) => {
    const body = scheduleWriteBody(data);
    if (editing) {
      await updateQuery(editing.id, body);
    } else {
      await createQuery(body);
    }
  };

  const handleRunNow = async (id: number) => {
    setRunningIds((prev) => new Set(prev).add(id));
    try {
      await runNow(id);
    } finally {
      setRunningIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleToggleActive = async (q: ScheduledQueryResponse) => {
    if (q.is_active) {
      await updateQuery(q.id, { is_active: false });
    } else {
      await updateQuery(q.id, { is_active: true });
    }
  };

  const handleShowHistory = (q: ScheduledQueryResponse) => {
    setHistoryQueryId(q.id);
    setHistoryQueryName(q.name);
    fetchHistory(q.id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await removeQuery(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // error handled by store
    } finally {
      setDeleting(false);
    }
  };

  const sqT = (t as any).scheduledQueries;

  return (
    <div>
      {/* Scheduled Queries Summary Widget */}
      <ScheduledQuerySummary className="mb-6" />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {sqT?.title ?? 'Scheduled Queries'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {sqT?.description ?? 'Automate recurring queries on a schedule'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchQueries()}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title={t.common.refresh}
          >
            <RefreshCw className={clsx('h-5 w-5', loading && 'animate-spin')} />
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            {sqT?.create ?? 'Create'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md flex items-center justify-between">
          <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
          <button onClick={clearError} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && queries.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      )}

      {/* Empty state */}
      {!loading && queries.length === 0 && (
        <div className="text-center py-12">
          <Clock className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {sqT?.noQueries ?? 'No scheduled queries yet'}
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            {sqT?.noQueriesHint ?? 'Create your first scheduled query to automate recurring tasks.'}
          </p>
        </div>
      )}

      {/* Query list */}
      {queries.length > 0 && (
        <div className="space-y-3">
          {queries.map((q) => (
            <div
              key={q.id}
              className={clsx(
                'border rounded-lg transition-colors',
                q.is_active
                  ? 'border-gray-200 dark:border-gray-700'
                  : 'border-gray-100 dark:border-gray-800 opacity-60'
              )}
            >
              {/* Row header */}
              <div className="flex items-center gap-3 p-4">
                <button
                  onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {expandedId === q.id ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronRight className="h-5 w-5" />
                  )}
                </button>

                {statusIcon(q.last_status)}

                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-white truncate">
                    {q.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-3 mt-0.5">
                    <span className="font-mono">{cronToHuman(q.cron_expression, sqT ?? {})}</span>
                    {q.scenario_id && (
                      <>
                        <span>|</span>
                        <span>{scenarios.find((s) => s.id === q.scenario_id)?.title ?? q.scenario_id}</span>
                      </>
                    )}
                    <span>|</span>
                    <span>{sqT?.lastRun ?? 'Last'}: {formatDateTime(q.last_run_at)}</span>
                    <span>|</span>
                    <span>{sqT?.nextRun ?? 'Next'}: {formatDateTime(q.next_run_at)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleRunNow(q.id)}
                    disabled={runningIds.has(q.id)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md disabled:opacity-50"
                    title={sqT?.runNow ?? 'Run Now'}
                  >
                    {runningIds.has(q.id) ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleShowHistory(q)}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                    title={sqT?.history ?? 'History'}
                  >
                    <History className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(q)}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                    title={t.common.edit}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(q)}
                    className={clsx(
                      'p-1.5 rounded-md',
                      q.is_active
                        ? 'text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/30'
                        : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                    )}
                    title={q.is_active ? (sqT?.deactivate ?? 'Deactivate') : (sqT?.activate ?? 'Activate')}
                  >
                    {q.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => setDeleteTarget(q)}
                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md"
                    title={sqT?.remove ?? 'Delete'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Expanded result */}
              {expandedId === q.id && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {q.scenario_id ? (
                      <>
                        {sqT?.modeScenario ?? 'Scenario'}:{' '}
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {scenarios.find((s) => s.id === q.scenario_id)?.title ?? q.scenario_id}
                        </span>
                      </>
                    ) : (
                      <>
                        {sqT?.queryText ?? 'Query'}: <span className="font-mono">{q.query_text}</span>
                      </>
                    )}
                  </div>
                  {q.notification_method === 'telegram' && (
                    <div className="text-xs text-blue-500 mb-2">
                      📱 {sqT?.telegramNotificationsEnabled ?? 'Telegram notifications enabled'}
                    </div>
                  )}
                  {q.last_result ? (
                    <pre className="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded p-3 overflow-x-auto max-h-60 whitespace-pre-wrap">
                      {q.last_result}
                    </pre>
                  ) : (
                    <p className="text-xs text-gray-400 italic">
                      {sqT?.noResult ?? 'No results yet'}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <FormModal
        open={showForm}
        editing={editing}
        scenarios={scenarios}
        scenariosLoading={scenariosLoading}
        scenariosError={scenariosError}
        onClose={() => setShowForm(false)}
        onSave={handleSave}
        error={error}
        clearError={clearError}
      />

      <HistoryModal
        open={historyQueryId !== null}
        queryName={historyQueryName}
        history={selectedQueryHistory}
        loading={historyLoading}
        onClose={() => setHistoryQueryId(null)}
      />

      <DeleteConfirmModal
        open={deleteTarget !== null}
        queryName={deleteTarget?.name ?? ''}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        deleting={deleting}
      />
    </div>
  );
};
